import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  registerTrackingEmit,
  registerTrackingFlush,
  trackEvent,
  flushTracking,
} from '@src/Tracking/trackEvent';
import {
  AMPLITUDE_DROP_LIST,
  getVendorsForEvent,
  isClientEmittable,
  MIGRATION_ALIASES,
  type RevenueProps,
  type TraceTokEventName,
  type TrackingPayload as CatalogPayload,
  type UserTraits,
  type VendorName,
} from '@src/Tracking/catalog';
import {enrichEvent} from '@src/Tracking/utils/enrich';
import {setUserId as persistUserId} from '@src/Tracking/utils/identity';

export type TrackingEventName = TraceTokEventName | string;
export type TrackingPayload = CatalogPayload;

export interface TrackingAdapter {
  /** Stable name. Used for per-vendor consent gating + routing. */
  name: VendorName | 'tealium' | 'console';
  /** Set to true if the adapter does NOT participate in the catalog routing
   * matrix (e.g. console logger, Tealium). Receives every event. */
  receivesAll?: boolean;
  /** Live consent check. Adapter is skipped if this returns false. */
  isEnabled?: () => boolean;
  track: (
    event: TrackingEventName,
    props?: TrackingPayload,
  ) => Promise<void> | void;
  identify?: (
    userId: string,
    traits?: UserTraits,
  ) => Promise<void> | void;
  trackRevenue?: (revenue: RevenueProps) => Promise<void> | void;
  flush?: () => Promise<void> | void;
}

type TrackingContextValue = {
  enabled: boolean;
  adapters: TrackingAdapter[];
  emit: (
    event: TrackingEventName,
    props?: TrackingPayload,
  ) => Promise<void>;
  /** Alias for emit() to ease migration from legacy `track(...)` callers. */
  track: (
    event: TrackingEventName,
    props?: TrackingPayload,
  ) => Promise<void>;
  identify: (userId: string, traits?: UserTraits) => Promise<void>;
  trackRevenue: (revenue: RevenueProps) => Promise<void>;
  flush: () => Promise<void>;
};

const noop = async () => {};

// Default emit/flush route through the module bridge. This is what a component
// receives when it renders OUTSIDE the TrackingProvider subtree (the vertical
// player, bookmarks, paywall, and checkout all do). Without this they would hit
// a silent no-op and their events would never fire. Once the provider mounts it
// registers the real emit/flush globally, so the bridge reaches the real
// pipeline regardless of where the calling component sits in the tree.
const bridgeEmit = async (
  event: TrackingEventName,
  props?: TrackingPayload,
) => {
  await trackEvent(event, props);
};
const bridgeFlush = async () => {
  await flushTracking();
};

const TrackingContext = createContext<TrackingContextValue>({
  enabled: false,
  adapters: [],
  emit: bridgeEmit,
  track: bridgeEmit,
  identify: noop,
  trackRevenue: noop,
  flush: bridgeFlush,
});

export const TrackingProvider: React.FC<{
  enabled?: boolean;
  adapters?: TrackingAdapter[];
  children: React.ReactNode;
}> = ({enabled = true, adapters = [], children}) => {
  // De-dup window: drops same event_id-less duplicates fired within 50ms.
  // (Single ref keeps the recent set without re-rendering.)
  const recentRef = useRef<Map<string, number>>(new Map());

  const isDuplicate = useCallback(
    (event: string, props: TrackingPayload | undefined) => {
      const key = `${event}:${JSON.stringify(props ?? {})}`;
      const now = Date.now();
      const last = recentRef.current.get(key);
      if (last && now - last < 50) return true;
      recentRef.current.set(key, now);
      // Keep map bounded
      if (recentRef.current.size > 200) {
        const first = recentRef.current.keys().next().value;
        if (first) recentRef.current.delete(first);
      }
      return false;
    },
    [],
  );

  const emit = useCallback<TrackingContextValue['emit']>(
    async (event, props) => {
      if (!enabled) return;
      if (typeof event !== 'string' || !event) return;

      // Guard: client cannot emit backend-source-of-truth events directly.
      // Logs once per event for debugging.
      if (!isClientEmittable(event)) {
        if (typeof console !== 'undefined') {
          console.warn(
            `[Tracking] "${event}" is backend-emitted; client emit blocked`,
          );
        }
        return;
      }

      if (isDuplicate(event, props)) return;

      const payload = enrichEvent(event, props);
      const allowedVendors = new Set<string>(getVendorsForEvent(event));

      const results = await Promise.allSettled(
        adapters.map(adapter => {
          if (adapter.isEnabled && !adapter.isEnabled()) return undefined;
          // Routing: receivesAll bypasses the matrix
          if (!adapter.receivesAll && !allowedVendors.has(adapter.name)) {
            return undefined;
          }
          return adapter.track(event, payload);
        }),
      );

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          // Swallow per-adapter errors so one vendor outage doesn't break the app.
          // eslint-disable-next-line no-console
          console.warn('[Tracking] adapter rejected', {
            adapter: adapters[index]?.name,
            event,
            reason: result.reason,
          });
        }
      });
    },
    [enabled, adapters, isDuplicate],
  );

  const identify = useCallback<TrackingContextValue['identify']>(
    async (userId, traits) => {
      if (!enabled || !userId) return;
      persistUserId(userId);
      await Promise.allSettled(
        adapters.map(adapter => {
          if (adapter.isEnabled && !adapter.isEnabled()) return undefined;
          return adapter.identify?.(userId, traits);
        }),
      );
    },
    [enabled, adapters],
  );

  const trackRevenue = useCallback<TrackingContextValue['trackRevenue']>(
    async revenue => {
      if (!enabled) return;
      await Promise.allSettled(
        adapters.map(adapter => {
          if (adapter.isEnabled && !adapter.isEnabled()) return undefined;
          return adapter.trackRevenue?.(revenue);
        }),
      );
    },
    [enabled, adapters],
  );

  const flush = useCallback<TrackingContextValue['flush']>(async () => {
    await Promise.allSettled(adapters.map(a => a.flush?.()));
  }, [adapters]);

  // Expose emit + flush to non-React code (RTK endpoints, plain utils) and to
  // components rendering outside this provider (via the default context bridge).
  // Declared after `flush` so its dependency is initialised (no TDZ).
  useEffect(() => {
    registerTrackingEmit(emit);
    registerTrackingFlush(flush);
  }, [emit, flush]);

  const value = useMemo<TrackingContextValue>(
    () => ({
      enabled,
      adapters,
      emit,
      track: emit,
      identify,
      trackRevenue,
      flush,
    }),
    [enabled, adapters, emit, identify, trackRevenue, flush],
  );

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTrackingContext = () => useContext(TrackingContext);

// Re-export drop-list + aliases so adapters in this package can import from
// a single point.
export {AMPLITUDE_DROP_LIST, MIGRATION_ALIASES};
